import React, {useState, useEffect, useContext} from 'react';
import {pushQueryParams} from 'client/history';
import {withRouter, WithRouterProps} from 'react-router';
import {DIMENSION_COLUMNS, columnIsAvailable} from 'client/Reports/reports.utils';
import usePrevious from 'lib/hooks/usePrevious';
import {NoDataMsg} from '@albert-io/atomic';

import {Column, Dimension, MetricColumn, SortOption} from '../../reports.types';
import {ReportsContext} from '../../Reports.context';

import './reports-table.scss';

import {ReportsPagination} from '../Pagination/ReportsPagination.react';

import HeaderRenderer from './HeaderRenderer';
import BodyRenderer from './BodyRenderer';

interface Props extends WithRouterProps {
  data: any[];
  loading: boolean;
  dimension: Dimension;
  metrics: MetricColumn[];
}

const rowSizeOptions = [{value: 25}, {value: 50}, {value: 100}];

const ReportsTable = withRouter(({data, dimension, metrics, location, loading}: Props) => {
  const {sort} = location.query;
  let initialSortBy = null;
  let initialSortDirection: SortOption = 'asc';
  if (sort) {
    const [querySortBy, querySortDirection] = sort.split(':');
    initialSortBy = querySortBy;
    initialSortDirection = querySortDirection;
  }

  const [sortBy, setSortBy] = useState<string | null>(initialSortBy);
  const [sortDirection, setSortDirection] = useState<SortOption>(initialSortDirection);
  const prevSortDirection = usePrevious(sortDirection);
  const prevSortBy = usePrevious(sortBy);

  useEffect(() => {
    if (sortBy && sortDirection && (sortBy !== prevSortBy || sortDirection !== prevSortDirection)) {
      pushQueryParams({sort: `${sortBy}:${sortDirection}`});
    }
  }, [sortBy, sortDirection, prevSortBy, prevSortDirection]);

  const {page, limit, totals, variables, topLevelFilterType} = useContext(ReportsContext);

  const total = totals.find((t) => t.type === dimension)?.value || 0;

  const dimensionColumns = DIMENSION_COLUMNS[dimension].filter((c) =>
    columnIsAvailable(c, [dimension], variables, topLevelFilterType)
  );

  const columns: Column[] = [
    ...dimensionColumns.filter((c) => !c.afterMetrics),
    ...metrics,
    ...dimensionColumns.filter((c) => c.afterMetrics)
  ];

  if (!loading && data.length === 0) {
    return (
      <div className='u-display_flex u-justify-content_center'>
        <NoDataMsg.Container centered>
          <NoDataMsg.Heading>No Data Available</NoDataMsg.Heading>
          <NoDataMsg.Body>There is no data available for the current filters.</NoDataMsg.Body>
        </NoDataMsg.Container>
      </div>
    );
  }

  return (
    <>
      <div className='reports-table__wrapper u-display_flex u-border u-border-radius_1 u-border-color_slate-300'>
        <table className='reports-table__table'>
          <HeaderRenderer
            columns={columns}
            sortBy={sortBy}
            setSortBy={(value) => {
              setSortBy(value);
            }}
            sortDirection={sortDirection}
            setSortDirection={(value) => {
              setSortDirection(value);
            }}
            dimensions={[dimension]}
            variables={variables}
            topLevelFilterType={topLevelFilterType}
          />

          <BodyRenderer
            columns={columns}
            data={data}
            dimensions={[dimension]}
            variables={variables}
            topLevelFilterType={topLevelFilterType}
          />
        </table>
      </div>
      <ReportsPagination limit={limit} page={page} total={total} rowSizeOptions={rowSizeOptions} />
    </>
  );
});

export default ReportsTable;
